/* eslint-disable guard-for-in */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-unused-vars */
/* eslint-disable max-lines */
/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

// import { IN_STOCK } from 'Component/ProductCard/ProductCard.config';
import { REVIEW_POPUP_ID } from 'Component/ProductReviews/ProductReviews.config';
import { showNotification } from 'Store/Notification/Notification.action';
import { showPopup } from 'Store/Popup/Popup.action';
import { isSignedIn } from 'Util/Auth';
// import {
//     BUNDLE,
//     CONFIGURABLE,
//     DOWNLOADABLE,
//     SIMPLE
// } from 'Util/Product';
import getStore from 'Util/Store';
import { getQueryParam, setQueryParams } from 'Util/Url';

import { RECENTLY_VIEWED_PRODUCTS_LIMIT } from './Types';

export const BUNDLE = 'bundle';
export const CONFIGURABLE = 'configurable';
export const DOWNLOADABLE = 'downloadable';
export const SIMPLE = 'simple';
export const ADD_TO_CART = 'ADD_TO_CART';
export const ADD_TO_WISHLIST = 'ADD_TO_WISHLIST';
export const GIFT_CARD = 'amgiftcard';
export const GROUPED = 'grouped';
export const VIRTUAL = 'virtual';

/**
 * Checks whether every option is in attributes
 * @param {Object} attributes
 * @param {{ attribute_code: string }[]} options
 * @returns {boolean}
 * @namespace Pwa/Util/Product/checkEveryOption */
export const checkEveryOption = (attributes, options) => Object.keys(options)
    .every((option) => {
        if (!attributes[option]) {
            return false;
        }

        const { attribute_value } = attributes[option];
        if (typeof options[option] === 'string') {
            return options[option] === attribute_value;
        }

        return options[option].includes(attribute_value);
    });

/** @namespace Pwa/Util/Product/getIndexedAttributeOption */
export const getIndexedAttributeOption = (option) => {
    const { swatch_data: defaultSwatchData } = option;
    if (!defaultSwatchData) {
        return option;
    }

    const { type } = defaultSwatchData;
    const swatch_data = type ? defaultSwatchData : null;

    return {
        ...option,
        swatch_data
    };
};

/** @namespace Pwa/Util/Product/getIndexedAttributes */
export const getIndexedAttributes = (attributes) => attributes.reduce((indexedAttributes, attribute) => {
    const { attribute_code, attribute_options = [] } = attribute;

    return {
        ...indexedAttributes,
        [attribute_code]: {
            ...attribute,
            attribute_options: attribute_options.reduce((acc, option) => {
                const { value } = option;

                return {
                    ...acc,
                    [value]: getIndexedAttributeOption(option)
                };
            }, {})
        }
    };
}, {});

/** @namespace Pwa/Util/Product/getIndexedConfigurableOptions */
export const getIndexedConfigurableOptions = (configurableOptions, indexedAttributes) => (
    configurableOptions.reduce((indexedConfigurableOptions, configurableOption) => {
        const { values, attribute_code } = configurableOption;

        return {
            ...indexedConfigurableOptions,
            [attribute_code]: {
                ...configurableOption,
                ...indexedAttributes[attribute_code],
                attribute_values: values.map(({ value_index }) => `${ value_index }`)
            }
        };
    }, {})
);

/** @namespace Pwa/Util/Product/getIndexedVariants */
export const getIndexedVariants = (variants) => variants.map(({ product }) => {
    const { attributes } = product;

    return {
        ...product,
        attributes: getIndexedAttributes(attributes || [])
    };
});

/** @namespace Pwa/Util/Product/getIndexedSingleVariant */
export const getIndexedSingleVariant = (variants, itemSku) => {
    const index = variants.findIndex(({ product: { sku } }) => sku === itemSku);

    if (index < 0) {
        return getIndexedVariants(variants);
    }

    const indexedProduct = variants[index].product;
    const { attributes } = indexedProduct;

    return [
        { ...indexedProduct, attributes: getIndexedAttributes(attributes || []) }
    ];
};

/** @namespace Pwa/Util/Product/getVariantsIndexes */
export const getVariantsIndexes = (variants, options, inStockOnly = false) => {
    const result = Object.entries(variants)
        .reduce((indexes, [index, variant]) => {
            if (checkEveryOption(variant.attributes, options)) {
                indexes.push(+index);
            }

            return indexes;
        }, []);

    // if (inStockOnly) {
    //     return result.filter((n) => variants[n].stock_status === IN_STOCK);
    // }

    return result;
};

/**
 * Get product variant index by options
 * @param {Object[]} variants
 * @param {{ attribute_code: string }[]} options
 * @pram {boolean} inStockOnly
 * @returns {number}
 * @namespace Pwa/Util/Product/getVariantIndex */
export const getVariantIndex = (variants, options, inStockOnly = false) => {
    const indexes = getVariantsIndexes(variants, options, inStockOnly);

    return indexes.length ? indexes[0] : -1;
};

/** @namespace Pwa/Util/Product/getIndexedCustomOption */
export const getIndexedCustomOption = (option) => {
    const {
        checkboxValues,
        dropdownValues,
        fieldValues,
        areaValues,
        fileValues,
        ...otherFields
    } = option;

    if (checkboxValues) {
        const data = Array.isArray(checkboxValues) ? checkboxValues : [checkboxValues];

        return { type: 'checkbox', data, ...otherFields };
    }

    if (dropdownValues) {
        const data = Array.isArray(dropdownValues) ? dropdownValues : [dropdownValues];

        return { type: 'dropdown', data, ...otherFields };
    }

    if (fieldValues) {
        const data = Array.isArray(fieldValues) ? fieldValues : [fieldValues];

        return { type: 'field', data, ...otherFields };
    }

    if (areaValues) {
        const data = Array.isArray(areaValues) ? areaValues : [areaValues];

        return { type: 'area', data, ...otherFields };
    }

    if (fileValues) {
        const data = Array.isArray(fileValues) ? fileValues : [fileValues];

        return { type: 'file', data, ...otherFields };
    }

    // skip unsupported types
    return null;
};

/** @namespace Pwa/Util/Product/getIndexedCustomOptions */
export const getIndexedCustomOptions = (options) => options.reduce(
    (acc, option) => {
        const indexedOption = getIndexedCustomOption(option);

        if (indexedOption) {
            acc.push(indexedOption);
        }

        return acc;
    },
    []
);

/** @namespace Pwa/Util/Product/getIndexedReviews */
export const getIndexedReviews = (reviews) => {
    if (!reviews) {
        return null;
    }

    const { items } = reviews;
    const ONE_FIFTH_OF_A_HUNDRED = 20;

    return items.reduce((acc, review) => {
        const { rating_votes = [], ...restOfReview } = review;

        const newRatingVotes = rating_votes.reduce((acc, vote) => {
            const { rating_code, value } = vote;

            return [
                ...acc,
                {
                    rating_code,
                    value,
                    // stars / 5 * 100 to get percent
                    percent: value * ONE_FIFTH_OF_A_HUNDRED
                }
            ];
        }, []);

        return [
            ...acc,
            {
                ...restOfReview,
                rating_votes: newRatingVotes
            }
        ];
    }, []);
};

/** @namespace Pwa/Util/Product/getBundleOptions */
export const getBundleOptions = (options, items) => {
    const bundleOptions = options.reduce((prev, next) => [...prev, ...next.selection_details], []);

    return items.map((item) => ({
        ...item,
        options: item?.options?.map((option) => {
            const selection = bundleOptions.find((o) => o.selection_id === option.id) || {};
            const {
                regular_option_price: regularOptionPrice = 0,
                regular_option_price_excl_tax: regularOptionPriceExclTax = 0,
                final_option_price: finalOptionPrice = 0,
                final_option_price_excl_tax: finalOptionPriceExclTax = 0
            } = selection;

            return {
                ...option,
                regularOptionPrice,
                regularOptionPriceExclTax,
                finalOptionPrice,
                finalOptionPriceExclTax
            };
        })
    }));
};

/** @namespace Pwa/Util/Product/getIndexedProduct */
export const getIndexedProduct = (product, itemSku) => {
    const {
        variants: initialVariants = [],
        configurable_options: initialConfigurableOptions = [],
        attributes: initialAttributes = [],
        options: initialOptions = [],
        rating_summary,
        review_count,
        reviews: initialReviews,
        items = [],
        bundle_options = []
    } = product;

    const attributes = getIndexedAttributes(initialAttributes || []);
    const reviews = getIndexedReviews(initialReviews);

    const updatedProduct = {
        ...product,
        configurable_options: getIndexedConfigurableOptions(initialConfigurableOptions, attributes),
        variants: itemSku ? getIndexedSingleVariant(initialVariants, itemSku) : getIndexedVariants(initialVariants),
        options: getIndexedCustomOptions(initialOptions || []),
        attributes,
        // Magento 2.4.1 review endpoint compatibility
        reviews,
        review_summary: {
            rating_summary,
            review_count
        }
    };

    if (bundle_options.length) {
        updatedProduct.items = getBundleOptions(bundle_options, items);
    }

    return updatedProduct;
};

/** @namespace Pwa/Util/Product/getIndexedProducts */
export const getIndexedProducts = (products) => products.map((product) => getIndexedProduct(product));

/** @namespace Pwa/Util/Product/getIndexedParameteredProducts */
export const getIndexedParameteredProducts = (products) => Object.entries(products)
    .reduce((products, [id, product]) => ({
        ...products,
        [id]: getIndexedProduct(product)
    }), {});

/** @namespace Pwa/Util/Product/getExtensionAttributes */
export const getExtensionAttributes = (product) => {
    const {
        configurable_options,
        configurableVariantIndex,
        productOptions,
        productOptionsMulti,
        downloadableLinks,
        variants,
        type_id,
        configurable_product_options_selection,
        sku
    } = product;

    if (type_id === CONFIGURABLE) {
        if (sku === 'KSDR002CON' || sku === 'KRLBCUSTOM') {
            const selected_options = [];
            const { options_available_for_selection = [] } = configurable_product_options_selection || {};
            for (const data of options_available_for_selection) {
                const { option_value_uids = [] } = data || {};
                selected_options.push(option_value_uids[0]);
            }

            return selected_options;
        }

        // const { attributes = {} } = product;
        const { attributes = {} } = variants[configurableVariantIndex] || {};

        const colorFromQueryParams = getQueryParam('color', location) || 1;
        const colorSizeParams = getQueryParam('tshirt_size', location) || 1;
        const properties = {
            configurable_item_options: Object.values(configurable_options)
                .reduce((prev, { attribute_id, attribute_code }) => {
                    const {
                        attribute_value,
                        attribute_id: attrId
                    } = attributes[attribute_code] || {};

                    if (attribute_value) {
                        return [
                            ...prev,
                            {
                                option_id: attribute_id || attrId,
                                option_value: attribute_value
                            }
                        ];
                    }

                    if (colorFromQueryParams !== 1) {
                        return [
                            ...prev,
                            {
                                option_id: attribute_id || attrId,
                                option_value: colorFromQueryParams
                            }
                        ];
                    }

                    if (colorSizeParams !== 1) {
                        return [
                            ...prev,
                            {
                                option_id: attribute_id || attrId,
                                option_value: colorSizeParams
                            }
                        ];
                    }

                    return prev;
                }, [])
        };

        if (productOptions) {
            properties.customizable_options = productOptions;
        }
        if (productOptionsMulti) {
            properties.customizable_options_multi = productOptionsMulti;
        }

        return properties;
        // console.log('check', selected_options);
        // return selected_options;
    }

    if (type_id === BUNDLE && (productOptions || productOptionsMulti)) {
        return { bundle_options: Array.from(productOptions || []) };
    }

    if (type_id === SIMPLE && (productOptions || productOptionsMulti)) {
        return {
            customizable_options: productOptions || [],
            customizable_options_multi: productOptionsMulti || []
        };
    }

    if (type_id === DOWNLOADABLE && downloadableLinks) {
        return {
            downloadable_product_links: downloadableLinks
        };
    }

    return {};
};

/** @namespace Pwa/Util/Product/sortBySortOrder */
export const sortBySortOrder = (options, sortKey = 'sort_order') => options.sort(
    (a, b) => {
        if (a[sortKey] < b[sortKey]) {
            return -1;
        }

        if (a[sortKey] > b[sortKey]) {
            return 1;
        }

        return 0;
    }
);

/** @namespace Pwa/Util/Product/getIsConfigurableParameterSelected */
// eslint-disable-next-line max-len
export const getIsConfigurableParameterSelected = (parameters, key, value) => Object.hasOwnProperty.call(parameters, key) && parameters[key] === value;

/** @namespace Pwa/Util/Product/getNewParameters */
export const getNewParameters = (parameters, key, value) => {
    // If value is already selected, than we remove the key to achieve deselection
    if (getIsConfigurableParameterSelected(parameters, key, value)) {
        const { [key]: oldValue, ...newParameters } = parameters;

        return newParameters;
    }

    return {
        ...parameters,
        [key]: value.toString()
    };
};

/** @namespace Pwa/Util/Product/showNewReviewPopup */
export const showNewReviewPopup = () => {
    const store = getStore();
    const {
        ConfigReducer: {
            reviews_allow_guest: isGuestEnabled
        } = {}
    } = store.getState();
    const { dispatch } = store;

    // if not logged in and guest reviews are not enabled
    if (!isSignedIn() && !isGuestEnabled) {
        dispatch(showNotification('info', __('You must login or register to review products.')));

        return;
    }

    dispatch(showPopup(REVIEW_POPUP_ID, { title: __('Write a review') }));
};

/** @namespace Pwa/Util/Product/getBooleanLabel */
export const getBooleanLabel = (label, isBoolean = false) => {
    if (!isBoolean) {
        return label;
    }

    return +label ? __('Yes') : __('No');
};

/** @namespace Pwa/Util/Product/validateProductQuantity */
export const validateProductQuantity = (quantity, stockItem) => {
    const { min_sale_qty = 1, max_sale_qty, qty_increments = 1 } = stockItem;

    if (quantity < min_sale_qty) {
        return [false, __('The minimum amount you can purchase is %s', min_sale_qty)];
    }

    if (quantity > max_sale_qty) {
        return [false, __('The maximum amount you can purchase is %s', max_sale_qty)];
    }

    if (qty_increments > 1 && quantity % qty_increments !== 0) {
        return [false, __('You can buy this product only in quantities of %s at a time.', qty_increments)];
    }

    return [true];
};

/** @namespace Pwa/Util/Product/addToRecentlyViewedProducts */
export const addToRecentlyViewedProducts = (sku) => {
    const viewedProduct = [sku];
    let recentlyViewed = localStorage.getItem('recently_viewed_product');

    if (recentlyViewed) {
        try {
            recentlyViewed = JSON.parse(recentlyViewed);
            recentlyViewed = Array.isArray(recentlyViewed) ? recentlyViewed : [];
        } catch (e) {
            console.log('Error parsing json of recently viewed items');
            recentlyViewed = [];
        }

        const checkProductDuplicate = Array.isArray(recentlyViewed) ? recentlyViewed?.includes(sku) : false;

        if (checkProductDuplicate) {
            return true;
        }

        if (recentlyViewed.length <= RECENTLY_VIEWED_PRODUCTS_LIMIT && !checkProductDuplicate) {
            recentlyViewed.unshift(sku);
            return (
                localStorage.setItem('recently_viewed_product', JSON.stringify(recentlyViewed))
            );
        }
        if (recentlyViewed.length > RECENTLY_VIEWED_PRODUCTS_LIMIT && !checkProductDuplicate) {
            recentlyViewed.unshift(sku);
            recentlyViewed.pop();
            return (
                localStorage.setItem('recently_viewed_product', JSON.stringify(recentlyViewed))
            );
        }
    }

    return (
        localStorage.setItem('recently_viewed_product', JSON.stringify(viewedProduct))
    );
};

/** @namespace Pwa/Util/Product/getAttributesWithValues */
export const getAttributesWithValues = (product) => {
    const { attributes = {}, parameters = {} } = product;

    return Object.entries(attributes).reduce((acc, [key, val]) => {
        const { attribute_label, attribute_value } = val;

        if (attribute_value) {
            return { ...acc, [attribute_label]: val };
        }

        const valueIndexFromParameter = parameters[key];

        if (valueIndexFromParameter) {
            return { ...acc, [attribute_label]: { ...val, attribute_value: valueIndexFromParameter } };
        }

        return acc;
    }, {});
};
